import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Check, Filter, RotateCcw, SlidersHorizontal, X } from "lucide-react";
import { useSettings } from "context/SettingsContext";
import { resolveDatePrefs } from "services/date-converter";
import useAdvanceFilterOptions from "./useAdvanceFilterOptions";
import FilterSection from "./FilterSection";
import FilterField from "./FilterField";
import FilterSummaryBar from "./FilterSummaryBar";
import { buildFilterPayload, countActive, hasValue, rangeError } from "./advance-filter.helpers";

// Global advanced filter: a "Filters" button that opens a right-side drawer
// listing the page's filterable columns as collapsible, icon-headed sections
// (icons by group name, see FilterSection.jsx). Every field shows the editor
// for its type (date range, number, text, dropdown, Any / Yes / No). Each page
// passes its own fields and sends the result to its own get-all API.
//
// Usage (field types are documented in advance-filter.helpers.js):
//   <GlobalAdvanceFilter
//     fields={clientFilterFields}                      // which columns, their types, and `group`
//     dependenciesLoader={loadClientFilterDependencies} // dropdown data, fetched when the drawer first opens
//     onApply={({ search }) => setFilters(search)}
//     resetKey={resetTrigger}                          // bump to clear (e.g. on Refresh)
//   />
//
// The page then posts `search: JSON.stringify(search)`; the backend's
// services/advance-filter/build-where.js turns it into a Prisma `where`.
const GlobalAdvanceFilter = ({
  fields = [],
  dependenciesLoader,
  onApply,
  resetKey,
  title = "Filters",
}) => {
  const settingsCtx = useSettings();
  const { timeZone, timeFormat } = resolveDatePrefs(settingsCtx?.settings);

  // Sections in the order their first field appears; fields without a `group` share an untitled one.
  const groups = useMemo(() => {
    const result = [];
    for (const field of fields) {
      const groupTitle = field.group || "";
      let group = result.find((item) => item.title === groupTitle);
      if (!group) {
        group = { title: groupTitle, fields: [] };
        result.push(group);
      }
      group.fields.push(field);
    }
    return result;
  }, [fields]);

  const [isOpen, setIsOpen] = useState(false);
  // What the table is currently filtered by.
  const [applied, setApplied] = useState({});
  // What is being edited in the drawer; only reaches the table on Apply.
  const [draft, setDraft] = useState({});
  // Collapsed sections by group title; kept between opens.
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const { optionsByKey, loading } = useAdvanceFilterOptions(fields, dependenciesLoader, isOpen, resetKey);

  useEffect(() => {
    if (resetKey === undefined) return;
    setApplied({});
    setDraft({});
  }, [resetKey]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen]);

  const appliedCount = countActive(applied, fields);
  const draftCount = countActive(draft, fields);
  const errors = useMemo(() => {
    const result = {};
    for (const field of fields) {
      const error = rangeError(field, draft[field.key]);
      if (error) result[field.key] = error;
    }
    return result;
  }, [fields, draft]);
  const hasErrors = Object.keys(errors).length > 0;

  // Every open starts from what is applied, so closing without Apply discards edits.
  const openDrawer = () => {
    setDraft(applied);
    setIsOpen(true);
  };

  const emit = (values) => {
    setApplied(values);
    onApply?.(buildFilterPayload(values, fields, timeZone));
  };

  const handleApply = () => {
    if (hasErrors) return;
    emit(draft);
    setIsOpen(false);
  };

  // Footer "Reset" and the toolbar's X: remove every filter from the table now.
  const handleReset = () => {
    setDraft({});
    setIsOpen(false);
    // Only refetch when something was actually filtering the table.
    if (appliedCount > 0) emit({});
  };

  // Summary bar "Clear All": empty the selection only; Apply still commits it.
  const clearDraft = () => setDraft({});

  const setFieldValue = (key, value) =>
    setDraft((prev) => {
      const next = { ...prev };
      if (value === undefined) delete next[key];
      else next[key] = value;
      return next;
    });

  const toggleGroup = (groupTitle) =>
    setCollapsedGroups((prev) => ({ ...prev, [groupTitle]: !prev[groupTitle] }));

  return (
    <>
      <div className="shrink-0 inline-flex items-center">
        <button
          type="button"
          onClick={openDrawer}
          onMouseDown={(e) => e.preventDefault()}
          aria-expanded={isOpen}
          title="Advanced filters"
          className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium border rounded-md hover:bg-surface_alt ${
            appliedCount > 0
              ? "text-primary bg-primary_tint border-border rounded-r-none border-r-0"
              : "text-text_secondary bg-surface border-border"
          }`}
        >
          <SlidersHorizontal size={14} />
          <span className="hidden sm:inline">Filters</span>
          {appliedCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-primary text-white text-[10px] font-semibold leading-none">
              {appliedCount}
            </span>
          )}
        </button>
        {appliedCount > 0 && (
          <button
            type="button"
            onClick={handleReset}
            title="Clear all filters"
            aria-label="Clear all filters"
            className="inline-flex items-center justify-center px-2 py-2 text-xs text-primary bg-primary_tint border border-border rounded-md rounded-l-none hover:bg-surface_alt"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {isOpen &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-[198] bg-black/30 backdrop-blur-[2px]"
              onClick={() => setIsOpen(false)}
              aria-hidden="true"
            />

            <aside
              role="dialog"
              aria-modal="true"
              aria-label={title}
              className="fixed z-[199] inset-y-0 right-0 w-full sm:inset-y-2 sm:right-2 sm:w-[420px] flex flex-col overflow-hidden bg-surface_alt border-l border-border sm:border sm:rounded-xl shadow-2xl slide-in"
            >
              <header className="flex items-center gap-3 px-4 py-3.5 bg-surface border-b border-border">
                <span className="flex-shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-lg bg-primary_tint text-primary">
                  <Filter size={17} />
                </span>
                <div className="flex-1 min-w-0">
                  <h2 className="text-[15px] font-semibold text-text_primary leading-tight">{title}</h2>
                  <p className="mt-0.5 text-xs text-text_muted leading-tight truncate">
                    {loading ? "Loading options…" : "Refine the list with any combination of filters"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  aria-label="Close filters"
                  className="flex-shrink-0 -mr-1 p-1.5 rounded-md text-text_muted hover:bg-surface_alt hover:text-text_primary"
                >
                  <X size={18} />
                </button>
              </header>

              <FilterSummaryBar
                fields={fields}
                draft={draft}
                optionsByKey={optionsByKey}
                errors={errors}
                timeFormat={timeFormat}
                onClearField={(key) => setFieldValue(key, undefined)}
                onClearAll={clearDraft}
              />

              <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3">
                {groups.map((group) => (
                  <FilterSection
                    key={group.title || "fields"}
                    title={group.title}
                    activeCount={group.fields.filter((field) => hasValue(field, draft[field.key])).length}
                    collapsed={!!collapsedGroups[group.title]}
                    onToggle={() => toggleGroup(group.title)}
                  >
                    {group.fields.map((field) => (
                      <FilterField
                        key={field.key}
                        field={field}
                        value={draft[field.key]}
                        active={hasValue(field, draft[field.key])}
                        onChange={(value) => setFieldValue(field.key, value)}
                        options={optionsByKey[field.key]}
                        loading={loading}
                        error={errors[field.key]}
                        timeFormat={timeFormat}
                      />
                    ))}
                  </FilterSection>
                ))}
              </div>

              <footer className="grid grid-cols-3 gap-2 px-4 py-3 bg-surface border-t border-border">
                <button
                  type="button"
                  onClick={handleReset}
                  disabled={draftCount === 0 && appliedCount === 0}
                  title="Remove all filters"
                  className="inline-flex items-center justify-center gap-1.5 h-9 text-[13px] font-medium text-text_secondary bg-surface border border-border rounded-md hover:bg-surface_alt hover:text-text_primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RotateCcw size={14} />
                  Reset
                </button>
                <button
                  type="button"
                  onClick={handleApply}
                  disabled={hasErrors}
                  className="col-span-2 inline-flex items-center justify-center gap-1.5 h-9 text-[13px] font-semibold text-white bg-primary border border-primary rounded-md hover:bg-primary_hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Check size={15} />
                  Apply Filters
                </button>
              </footer>
            </aside>
          </>,
          document.body
        )}
    </>
  );
};

export default GlobalAdvanceFilter;
